import { toast } from "sonner"

/**
 * Contrato de errores del front.
 *
 * El interceptor de `src/lib/axios.ts` garantiza que TODA promesa rechazada
 * llega al catch como un objeto plano con esta forma:
 *
 *   { message: string, httpStatus?: number, connectionFailed?: true, ...body }
 *
 * - `message` siempre existe (el interceptor lo rellena si el backend manda
 *   HTML o un string).
 * - `httpStatus` solo en errores HTTP reales (401/403/404/422/500...).
 * - `connectionFailed: true` cuando ni siquiera hubo respuesta (red, backend
 *   caído, URL incorrecta).
 *
 * OJO: como el interceptor ya despojó `error.response`, leer
 * `error.response?.data?.message` en un catch es SIEMPRE undefined — patrón
 * prohibido; usar `extractErrorMessage`.
 */

/** Mensaje por defecto según el código HTTP cuando el backend no manda uno útil. */
export function defaultMessageForStatus(status?: number): string {
  switch (status) {
    case 401:
      return "Tu sesión expiró. Vuelve a iniciar sesión."
    case 403:
      return "No tienes permiso para realizar esta operación."
    case 404:
      return "El recurso solicitado no existe."
    case 413:
      return "El archivo o la solicitud es demasiado grande."
    case 422:
      return "Los datos enviados no son válidos. Revisa el formulario."
    case 429:
      return "Demasiadas solicitudes. Espera un momento e intenta de nuevo."
    default:
      if (status && status >= 500) {
        return "Error interno del servidor. Intenta de nuevo en unos minutos."
      }
      return "Ocurrió un error inesperado. Intenta de nuevo."
  }
}

interface ApiErrorShape {
  message?: unknown
  httpStatus?: unknown
  connectionFailed?: unknown
  errors?: unknown
}

function firstFieldError(errors: unknown): string | null {
  if (!errors || typeof errors !== "object" || Array.isArray(errors)) return null
  const obj = errors as Record<string, unknown>
  for (const key of Object.keys(obj)) {
    const val = obj[key]
    if (Array.isArray(val) && typeof val[0] === "string" && val[0].trim()) {
      return val[0]
    }
    if (typeof val === "string" && val.trim()) return val
  }
  return null
}

/**
 * Extrae el mensaje más útil de cualquier rechazo de la capa API.
 * Cadena de prioridad: `message` del backend → primer error de campo de
 * `errors` → fallback por `httpStatus` → `fallback` del caller.
 */
export function extractErrorMessage(error: unknown, fallback?: string): string {
  if (!error || typeof error !== "object") {
    return typeof error === "string" && error.trim()
      ? error
      : (fallback ?? defaultMessageForStatus())
  }
  const e = error as ApiErrorShape
  if (typeof e.message === "string" && e.message.trim()) return e.message
  const fieldMsg = firstFieldError(e.errors)
  if (fieldMsg) return fieldMsg
  if (typeof e.httpStatus === "number") return defaultMessageForStatus(e.httpStatus)
  return fallback ?? defaultMessageForStatus()
}

/**
 * Toast de error estándar para catches de API. `context` es una frase corta
 * de qué se intentaba ("Guardar cliente") y antecede al detalle del backend:
 * "Guardar cliente: el MSISDN ya existe".
 */
export function toastApiError(error: unknown, context?: string): void {
  const msg = extractErrorMessage(error)
  toast.error(context ? `${context}: ${msg}` : msg)
}
